import React, { FC, useState } from 'react'

import {
  SUPERVISION_MODE,
  SUPERVISION_MODE_DESCRIPTION,
  SUPERVISION_MODE_LABEL,
  SUPERVISION_MODE_OPTIONS,
} from 'constants/supervisionMode'

import RequestSupervisionModeModal from './RequestSupervisionModeModal'
import * as S from './UsageMeter.style'

interface Props {
  metric: string
  meta?: { label: string }
  mode: string
  botId: string | number
  botName?: string
  requestedBy?: string
  /** Hidden for CareBrain operators, who change the mode directly instead. */
  canRequestIncrease?: boolean
  /** Package/bot feature flag — disables the request CTA when false. */
  featureEnabled?: boolean
}

// Same Lucide "users" glyph MeterGlyph draws for this metric elsewhere —
// duplicated rather than imported so this card has no dependency on the
// metered-usage rendering path it no longer uses.
const SupervisionGlyph: FC = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

/**
 * Supervision scheduling, as a card the customer reads rather than a dropdown
 * they edit. Dynamic vs Default used to save the instant a dropdown changed;
 * it now goes through the same request-and-confirm path as a usage-limit
 * increase (see RequestSupervisionModeModal) — a manual decision on the
 * CareBrain side either way, so the UI should not imply it happens on click.
 *
 * Package semantics: Default → only Default on; Dynamic → both Default and
 * Dynamic on (defaultEnabled + dynamicEnabled). Checkboxes are display-only.
 */
export const SupervisionModeCard: FC<Props> = ({
  metric,
  meta,
  mode,
  botId,
  botName,
  requestedBy,
  canRequestIncrease = true,
  featureEnabled = true,
}) => {
  const [requestOpen, setRequestOpen] = useState(false)
  const isDynamic = mode === SUPERVISION_MODE.DYNAMIC

  return (
    <>
      <S.SupervisionCard>
        <S.SupervisionHeaderRow>
          <S.CardHeadLeft>
            <S.MeterIcon>
              <SupervisionGlyph />
            </S.MeterIcon>
            <S.MeterIdentity>
              <S.MeterLabel>{meta?.label || metric}</S.MeterLabel>
            </S.MeterIdentity>
          </S.CardHeadLeft>

          {canRequestIncrease && (
            <S.GhostButton
              disabled={!featureEnabled}
              onClick={() => featureEnabled && setRequestOpen(true)}>
              Request change
            </S.GhostButton>
          )}
        </S.SupervisionHeaderRow>

        <S.SupervisionIntro>How this organisation&rsquo;s supervision sessions are managed.</S.SupervisionIntro>

        <S.OptionGrid>
          {SUPERVISION_MODE_OPTIONS.map(option => {
            // Matches package: defaultEnabled always on when a mode is set;
            // dynamicEnabled only when the org mode is Dynamic.
            const checked = option === SUPERVISION_MODE.DEFAULT || isDynamic
            return (
              <S.OptionCard key={option} $active={checked}>
                <S.OptionHeaderRow>
                  <S.OptionTitle>{SUPERVISION_MODE_LABEL[option]}</S.OptionTitle>
                  <S.OptionCheckbox $checked={checked} aria-hidden="true" />
                </S.OptionHeaderRow>
                <S.OptionDesc>{SUPERVISION_MODE_DESCRIPTION[option]}</S.OptionDesc>
              </S.OptionCard>
            )
          })}
        </S.OptionGrid>
      </S.SupervisionCard>

      {requestOpen && (
        <RequestSupervisionModeModal
          open={requestOpen}
          onClose={() => setRequestOpen(false)}
          botId={botId}
          botName={botName}
          requestedBy={requestedBy}
          mode={mode}
        />
      )}
    </>
  )
}

export default SupervisionModeCard
